/**
 * SegmentButton — canonical design-system segmented-control option (src/ui/ds).
 *
 * One option inside a SegmentedControl: quiet tertiary ink that lifts on hover,
 * primary ink when selected. Standalone it draws its own thumb treatment
 * (bg.component + seg-thumb shadow); SegmentedControl sets `showThumb` false
 * because it renders a single sliding thumb behind the row instead — the same
 * split PageTab/PageTabBar use for the selected underline. Theme-token driven.
 *
 * `rounded` switches the radius to the pill treatment (radius full) to match a
 * rounded SegmentedControl track.
 *
 *
 * PORTED VERBATIM from ploy-client src/ui/ds/SegmentButton.tsx — the canonical
 * design-system component. Do not restyle here; upstream owns it. Portal-local
 * changes only: dsTag inlined, viewer-only `whenToUse` export dropped.
 *
 * Location: src/components/ds/SegmentButton.tsx
 */
import { chakra } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Counter } from "./Counter";
import type { CounterTone } from "./Counter";

/* dev-only DOM marker (inlined from ploy-client's ds/dsTag) — compiles away
   in production builds. */
const dsTag = (name: string): Record<string, string> =>
  import.meta.env.DEV ? { "data-ds": name } : {};

export type SegmentButtonSize = "sm" | "md";

export interface SegmentButtonProps {
  label: string;
  /** optional leading glyph; sized by the button's font size (pass an 1em icon) */
  leadingIcon?: ReactNode;
  /** render only the leading icon — `label` becomes the aria-label */
  iconOnly?: boolean;
  /** trailing Counter count (e.g. per-view item totals); string for "3/13" */
  count?: number | string;
  /** trailing Counter tone — negative for error counts etc. */
  countTone?: CounterTone;
  isSelected?: boolean;
  isDisabled?: boolean;
  /** sm = compact rows (filter/condition builders); md = default */
  size?: SegmentButtonSize;
  /** pill treatment (radius full) — matches a rounded SegmentedControl track */
  rounded?: boolean;
  /** stretch to share the track's width equally — set by a fullWidth SegmentedControl */
  fullWidth?: boolean;
  /**
   * Draw the selected thumb treatment (bg.component + seg-thumb shadow) on the
   * button itself. Defaults true for standalone use; SegmentedControl sets it
   * false because it renders a single sliding thumb behind the row.
   */
  showThumb?: boolean;
  /** gallery only: pins the visual state for inspection */
  forceState?: "default" | "hover" | "disabled";
  onClick?: () => void;
}

export function SegmentButton({
  label,
  leadingIcon,
  iconOnly = false,
  count,
  countTone = "neutral",
  isSelected = false,
  isDisabled = false,
  size = "md",
  rounded = false,
  fullWidth = false,
  showThumb = true,
  forceState = "default",
  onClick,
}: SegmentButtonProps) {
  const disabled = isDisabled || forceState === "disabled";
  const hasCounter = !iconOnly && count != null;
  // Fixed heights (not vertical padding) so taller content — the trailing
  // Counter chip — centres inside the segment instead of inflating it.
  // With a counter active, only the TRAILING padding collapses to the chip's
  // vertical inset ((height − chip)/2 → 5px md / 3px sm) so the chip sits
  // evenly inset on three sides; the label keeps its standard breathing room.
  const sizeStyles =
    size === "sm"
      ? { h: "24px", pl: "sm", pr: hasCounter ? "3px" : "sm", fontSize: "xs" }
      : { h: "30px", pl: "md", pr: hasCounter ? "5px" : "md", fontSize: "md" };

  const ink = isSelected
    ? disabled
      ? "text.disabled.default"
      : "text.body.primary"
    : disabled
      ? "text.disabled.default"
      : "text.body.tertiary";

  // the selected thumb surface, drawn by the button itself only when standalone
  const thumb = showThumb && isSelected;

  return (
    <chakra.button
      {...dsTag("SegmentButton")}
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={isSelected}
      aria-label={iconOnly ? label : undefined}
      position="relative"
      zIndex={1}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="1xs"
      flex={fullWidth ? 1 : undefined}
      {...sizeStyles}
      borderRadius={rounded ? "full" : "component.md"}
      fontFamily="body"
      fontWeight={600}
      lineHeight="1"
      border="0"
      bg={thumb ? (disabled ? "bg.disabled" : "bg.component") : disabled && isSelected ? "bg.disabled" : "transparent"}
      boxShadow={thumb && !disabled ? "seg-thumb" : undefined}
      color={forceState === "hover" && !isSelected ? "text.body.primary" : ink}
      cursor={disabled ? "not-allowed" : "pointer"}
      transition="color 120ms"
      _hover={!disabled && !isSelected ? { color: "text.body.primary" } : undefined}
      _focusVisible={{ outline: "none", boxShadow: "0 0 0 3px rgba(88,97,222,0.25)" }}
      sx={{ "& svg": { display: "block" }, "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
    >
      {leadingIcon}
      {!iconOnly && label}
      {!iconOnly && count != null && <Counter count={count} tone={countTone} size={size} isDisabled={disabled} />}
    </chakra.button>
  );
}

export default SegmentButton;
